import { RadarBriefHistory } from '@/features/radar/RadarBriefHistory';
import { RadarMonitorWorkspace } from '@/features/radar/RadarMonitorWorkspace';
import { observeVisible, radarAnalytics, radarBrowserException } from '@/features/radar/radarAnalytics';
import { RadarLocaleProvider, useRadarLocale } from '@/i18n/RadarLocaleContext';
import { RadarAccountBar } from '@/features/radar/RadarAccountBar';
import { RadarQuickStartLink } from '@/features/radar/RadarQuickStartLink';
import { useRadarSignOut } from '@/features/radar/useRadarSignOut';
import { RadarDisplayProvider } from '@/features/radar/RadarDisplay';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, LoaderCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { RadarOutreachFlowProvider } from '@/features/radar/RadarOutreachFlow';
import { RadarOutreachPanel } from '@/features/radar/RadarOutreachPanel';
import {RadarWorkspaceView as LegacyWorkspaceView} from '@/features/radar/legacy/RadarWorkspaceView';
import {RadarOutreachPanel as LegacyOutreachPanel} from '@/features/radar/legacy/RadarOutreachPanel';
import {RadarJourneyProvider} from '@/features/radar/RadarJourney';
import { RadarActPanel } from '@/features/radar/RadarActPanel';
import { RadarEmailSignals } from '@/features/radar/RadarEmailSignals';
import { EmailSignalExportContext } from '@/features/radar/RadarEmailSignalNotice';
import '@/styles/radar-act.css';
import { RadarSignIn } from '@/features/radar/RadarSignIn';
import { radarApi } from '@/features/radar/radarApi';
import { useRadarUsage } from '@/features/radar/useRadarUsage';
import { useRadarBillingReturn } from '@/features/radar/useRadarBillingReturn';
import { RadarResearchSettings } from '@/features/radar/RadarResearchSettings';
import { GrowingTextarea, RadarWorkspaceView } from '@/features/radar/RadarWorkspaceView';
import {
  RADAR_POLL_INTERVAL_MS,
  shouldContinueRadarPolling,
} from '@/features/radar/radarPolling';
import type {
  MarketBrief,
  SignalFocus,
  RadarWorkspaceBundle,
  SearchStatus,
} from '@/features/radar/types';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { isConnectionError, isRetryableReadError } from '@/lib/connectionErrors';
import '@/styles/radar-workspace.css';

function friendlyError(error: unknown): string {
  if (!(error instanceof Error)) return 'Something went wrong. Please try again.';
  if (isConnectionError(error)) return 'Connection interrupted. Please try again.';
  if (error instanceof TypeError) return 'Something went wrong. Please try again.';
  return error.message;
}

function responseUncertain(error: unknown): boolean {
  // Keep mutation reconciliation conservative even when the UI cannot identify
  // a particular TypeError as a network error. A response may have been lost.
  return error instanceof TypeError || Number((error as { status?: number })?.status) >= 500;
}

function cleanBriefList(values: string[]): string[] {
  return values.map((value) => value.trim()).filter(Boolean);
}

function authCallbackMessage(): string {
  // Capture before the auth SDK initializes. Never render provider descriptions
  // or rewrite the URL: a successful callback may contain session credentials.
  for (const value of [window.location.hash.slice(1), window.location.search.slice(1)]) {
    const params = new URLSearchParams(value);
    if (params.get('error_code') === 'otp_expired') {
      return 'This sign-in link is invalid or has expired. Request an email verification code below.';
    }
    if (params.has('error') || params.has('error_code')) {
      return 'Sign-in could not be completed. Please try signing in again.';
    }
  }
  return '';
}

type PendingAction = 'brief' | 'confirm' | 'search' | 'feedback' | 'watch' | null;

export default function RadarWorkspacePage() {
  const [session, setSession] = useState<Session | null>(null);
  const { signOut, signingOut, signOutFailed } = useRadarSignOut(session, () => setSession(null));
  return <div className="radar-private"><RadarLocaleProvider>
    <RadarAccountBar signedIn={!!session} signingOut={signingOut} failed={signOutFailed} onSignOut={signOut}/>
    <RadarWorkspaceContent session={session} setSession={setSession} onSignOut={signOut} signingOut={signingOut}/>
  </RadarLocaleProvider></div>;
}

function RadarWorkspaceContent({ session, setSession, onSignOut, signingOut }: {
  session: Session | null;
  setSession: (session: Session | null) => void;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const { t, e, locale } = useRadarLocale();
  const { workspaceId } = useParams();
  const billingReturn = useRadarBillingReturn(workspaceId);
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [authAttempt, setAuthAttempt] = useState(0);
  const [authProblem, setAuthProblem] = useState<{ message: string; pending: boolean } | null>(null);
  const authPending = useRef(false);
  const [authCallbackError] = useState(authCallbackMessage);
  const [bundle, setBundle] = useState<RadarWorkspaceBundle | null>(null);
  const [bundleUserId, setBundleUserId] = useState<string | null>(null);
  const [editingBriefVersion,setEditingBriefVersion]=useState(false);
  const [selectedBriefVersion,setSelectedBriefVersion]=useState<number|null>(null);
  const selectedBriefVersionRef=useRef<number|null>(null);
  const [loadingVersion,setLoadingVersion]=useState(false);
  const [draftBrief, setDraftBrief] = useState<MarketBrief | null>(null);
  const [activeSection, setActiveSection] = useState<'brief' | 'discover' | 'watch' | 'act' | null>(null);
  const [actContact, setActContact] = useState({ id: '', revision: 0 });
  const [watchFocus, setWatchFocus] = useState<string>();
  const [actOpened, setActOpened] = useState(false);
  const [earlierActOpened, setEarlierActOpened] = useState(false);
  const [searchesRemaining, setSearchesRemaining] = useState<number>();
  const [failure, setFailure] = useState<{ message: string; status?: number; retryable?: boolean } | null>(null);
  const [feedbackError, setFeedbackError] = useState('');
  const error = failure?.message || '';
  const needsReconnect = failure?.status === 401;
  const [opening, setOpening] = useState(false);
  const [openingSlow, setOpeningSlow] = useState(false);
  const openingRef = useRef(false);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [pending, setPending] = useState<PendingAction>(null);
  const savingFeedback = pending === 'feedback';
  const savingWorkspace = savingFeedback || pending === 'confirm' || pending === 'watch';
  const pendingRef = useRef<PendingAction>(null);
  const searchKeys = useRef(new Map<number|null,string>());
  const pollingRun = useRef<{ id: string; startedAt: number } | null>(null);
  const operationScope = useRef({ active: true });
  const [connectionNotice, setConnectionNotice] = useState('');
  const reviewedBrief = useRef<string | null>(null);
  const [criteriaUpdated, setCriteriaUpdated] = useState(false);
  const token = session?.access_token;
  const liveUsage = useRadarUsage(token,workspaceId,bundle?.usage);
  const userId = session?.user.id;
  const authUser = useRef<string | null>(null);

  useEffect(() => {
    void radarAnalytics.start();
    const error = (event: ErrorEvent) => radarBrowserException(event.error?.name || 'Error');
    const rejection = () => radarBrowserException('Error', 'unhandledrejection');
    window.addEventListener('error', error);
    window.addEventListener('unhandledrejection', rejection);
    return () => { radarAnalytics.stop(); window.removeEventListener('error', error);
      window.removeEventListener('unhandledrejection', rejection); };
  }, []);
  useEffect(() => {
    radarAnalytics.identity(userId || null, !!bundle && bundleUserId === userId && bundle.workspace.id === workspaceId, bundle?.access?.analytics_cohort);
  }, [userId, bundleUserId, bundle?.workspace.id, bundle?.access?.analytics_cohort, workspaceId]);
  useEffect(() => {
    if (!authReady || opening) return;
    const screen = !session ? 'sign_in' : !bundle ? 'access'
      : !(draftBrief || bundle.workspace.brief || bundle.workspace.brief_hypothesis) ? 'brief_start' : null;
    const element = document.querySelector<HTMLElement>('.radar-private');
    if (screen && element) return observeVisible(element, () => radarAnalytics.screenViewed(screen, workspaceId));
  }, [authReady, opening, !!session, !!bundle, !!draftBrief, bundle?.workspace.brief, bundle?.workspace.brief_hypothesis, workspaceId]);

  function setError(message: string) {
    // A failed retry does not prove access recovered. Keep its recovery action
    // until an authenticated response succeeds or the account scope changes.
    setFailure((current) => current?.status ? current : message ? { message } : null);
  }

  function begin(action: PendingAction): boolean {
    if (pendingRef.current) return false;
    pendingRef.current = action;
    setPending(action);
    if (action === 'feedback') setFeedbackError('');
    else { setError(''); setConnectionNotice(''); }
    return true;
  }

  function finish(scope: { active: boolean }) {
    if (!scope.active) return;
    pendingRef.current = null;
    setPending(null);
  }

  function handleAccessError(error: unknown): boolean {
    if ((error as {code?: string})?.code === 'outcome_trial_expired') return false;
    const status = Number((error as { status?: number })?.status);
    if (status !== 401 && status !== 403) return false;
    setConnectionNotice('');
    setFailure({ status, message: status === 401
      ? 'Your session needs refreshing. Reconnect to check access again; your current work stays on this page.'
      : 'Access is no longer available for this account or workspace.' });
    // An expired token does not revoke access or erase already-loaded work.
    // Only an explicit access denial replaces the workspace with the access screen.
    if (status === 403) setBundle(null);
    return true;
  }

  useEffect(() => {
    const scope = { active: true };
    operationScope.current = scope;
    setBundle(null);
    setDraftBrief(null);
    setEditingBriefVersion(false);setSelectedBriefVersion(null);selectedBriefVersionRef.current=null;
    reviewedBrief.current = null;
    setCriteriaUpdated(false);
    setActiveSection(null);
    setFeedbackError('');
    setFailure(null);
    pollingRun.current = null;
    setConnectionNotice('');
    searchKeys.current.clear();
    finish(scope);
    return () => { scope.active = false; };
  }, [userId, workspaceId]);

  useEffect(() => {
    const previousTitle = document.title;
    const existingRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousRobots = existingRobots?.content;
    const robots = existingRobots || document.head.appendChild(document.createElement('meta'));
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');
    document.title = t('Buyer Radar Workspace | Mindstream');
    return () => {
      document.title = previousTitle;
      if (existingRobots && previousRobots !== undefined) existingRobots.content = previousRobots;
      else robots.remove();
    };
  }, [locale]);

  useEffect(() => {
    let active = true;
    let receivedEvent = false;
    let unsubscribe: (() => void) | undefined;
    authPending.current = true;
    setAuthProblem(null);
    const timer = window.setTimeout(() => {
      if (active && authPending.current) setAuthProblem({ pending: true,
        message: 'This request is taking longer than expected. Its result is not yet known.' });
    }, 15_000);
    function accept(nextSession: Session | null) {
      if (!active) return;
      window.clearTimeout(timer);
      authPending.current = false;
      authUser.current = nextSession?.user.id || null;
      setSession(nextSession);
      setAuthProblem(null);
      setAuthReady(true);
    }
    function fail(authError: unknown) {
      if (!active || receivedEvent) return;
      window.clearTimeout(timer);
      authPending.current = false;
      setAuthProblem({ pending: false, message: friendlyError(authError) });
    }
    try {
      const client = getSupabaseBrowserClient();
      void client.auth.getSession().then(({ data, error }) => {
        if (!active || receivedEvent) return;
        if (error) fail(error);
        else accept(data.session);
      }).catch(fail);
      const { data } = client.auth.onAuthStateChange((event, nextSession) => {
        if (!active) return;
        // The SDK's null INITIAL_SESSION carries no error information. Let the
        // explicit getSession result distinguish signed-out from failed reads;
        // genuine sign-in/sign-out/refresh events still supersede that result.
        if (event === 'INITIAL_SESSION' && !nextSession) return;
        receivedEvent = true;
        const nextUser = nextSession?.user.id || null;
        if (event === 'SIGNED_IN' && nextUser && nextUser !== authUser.current) {
          radarAnalytics.identity(nextUser, false);
          radarAnalytics.capture('radar_auth_result', { outcome: 'signed_in' });
        }
        accept(nextSession);
      });
      unsubscribe = () => data.subscription.unsubscribe();
    } catch (authError) {
      fail(authError);
    }
    return () => { active = false; window.clearTimeout(timer); unsubscribe?.(); };
  }, [authAttempt, setSession]);

  const loadWorkspace = useCallback((accessToken:string,id:string)=> selectedBriefVersionRef.current===null
    ? radarApi.getWorkspace(accessToken,id) : radarApi.getWorkspace(accessToken,id,selectedBriefVersionRef.current), []);

  const applyBundle = useCallback((next: RadarWorkspaceBundle) => {
    if(next.workspace.brief_email_enabled && (next.selected_brief_version??null)!==selectedBriefVersionRef.current)return;
    if (next.latest_run?.idempotency_key) radarAnalytics.bindSearch(next.latest_run.idempotency_key, next.latest_run.id);
    setBundleUserId(userId || null);
    setBundle(next);
    setFeedbackError('');
    setFailure((current) => current?.status ? null : current);
    if (next.access) setSearchesRemaining(next.access.searches_remaining);
    setConnectionNotice('');
  }, [userId]);

  useEffect(() => {
    if (!token) { openingRef.current = false; setOpening(false); setBundle(null); return; }
    let cancelled = false;
    setOpeningSlow(false);
    const timer = window.setTimeout(() => { if (!cancelled) setOpeningSlow(true); }, 15_000);
    async function open() {
      openingRef.current = true;
      setOpening(true);
      try {
        // Always open the Radar session first: this is the one-time invitation
        // and preloaded-workspace claim, including for a bookmarked workspace URL.
        const opened = await radarApi.openSession(token!,workspaceId);
        if (cancelled) return;
        setSearchesRemaining(opened.access.searches_remaining);
        if (!workspaceId) {
          navigate(`/buyer-radar/workspaces/${opened.workspace.id}`, { replace: true });
          return;
        }
        const next = await loadWorkspace(token!, workspaceId);
        if (!cancelled) {
          applyBundle(next);
          setFailure(null);
          setDraftBrief((current) => current || next.workspace.brief || next.workspace.brief_hypothesis || null);
        }
      } catch (openError) {
        if (!cancelled && !handleAccessError(openError)) {
          setFailure(current => current?.status ? current : {
            message: friendlyError(openError), retryable: isRetryableReadError(openError),
          });
        }
      } finally {
        window.clearTimeout(timer);
        if (!cancelled) { openingRef.current = false; setOpeningSlow(false); setOpening(false); }
      }
    }
    void open();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [token, userId, workspaceId, applyBundle, navigate, connectionAttempt]);

  useEffect(() => {
    const status = bundle?.latest_run?.status || 'idle';
    const runId = bundle?.latest_run?.id;
    if (runId && pollingRun.current?.id !== runId) {
      pollingRun.current = { id: runId, startedAt: Date.now() };
    }
    if (savingWorkspace || needsReconnect || !token || !workspaceId || (status !== 'queued' && status !== 'processing')) return;
    const pollingStartedAt = pollingRun.current?.startedAt ?? Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      if (!shouldContinueRadarPolling(status, Date.now() - pollingStartedAt)) {
        setConnectionNotice(
          'This search is taking longer than expected. Refresh to check its status; your previous results remain available.'
        );
        return;
      }
      try {
        const next = await loadWorkspace(token!, workspaceId!);
        if (cancelled) return;
        applyBundle(next);
        if (next.latest_run?.status !== 'queued' && next.latest_run?.status !== 'processing') return;
      } catch (pollError) {
        if (cancelled || handleAccessError(pollError)) return;
        setConnectionNotice('Reconnecting… Your search may still be running. We will check again automatically.');
      }
      if (!cancelled) timer = setTimeout(poll, RADAR_POLL_INTERVAL_MS);
    }
    timer = setTimeout(poll, RADAR_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bundle?.latest_run?.id, bundle?.latest_run?.status, applyBundle, token, workspaceId, needsReconnect, savingWorkspace]);

  useEffect(() => {
    if (activeSection !== 'watch' || savingWorkspace || needsReconnect || !token || !workspaceId
      || ['queued','processing'].includes(bundle?.latest_run?.status || '')) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function refreshWatch() {
      if (document.visibilityState === 'hidden') {
        if (!cancelled) timer = setTimeout(refreshWatch, 30000);
        return;
      }
      try {
        const next = await loadWorkspace(token!, workspaceId!);
        if (cancelled) return;
        applyBundle(next);
        setConnectionNotice('');
      } catch (refreshError) {
        if (cancelled || handleAccessError(refreshError)) return;
        setConnectionNotice('Could not refresh Watch. Your saved results remain here; checking again shortly.');
      }
      if (!cancelled) timer = setTimeout(refreshWatch, 30000);
    }
    timer = setTimeout(refreshWatch, 30000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeSection, savingWorkspace, needsReconnect, token, workspaceId, bundle?.latest_run?.status, applyBundle]);

  async function generateBrief(input: {
    seller_url?: string;
    target_market?: string;
    relationship_goal?: string;
    seller_notes?: string;
  }) {
    if (!token || !workspaceId || !begin('brief')) return;
    const scope = operationScope.current;
    try {
      const workspace = await radarApi.generateBrief(token, workspaceId, input);
      if (!scope.active) return;
      setFailure((current) => current?.status ? null : current);
      setBundle((current) => current ? { ...current, workspace } : current);
      setDraftBrief(workspace.brief_hypothesis || null);
      if (workspace.brief_provenance?.source_warning === 'website_unreadable') {
        setConnectionNotice('The website could not be read. This draft uses your product description.');
      }
    } catch (briefError) {
      if (!scope.active || handleAccessError(briefError)) return;
      // An explicit page-reader rejection is final; only uncertain delivery
      // needs reconciliation with a generation that may still be running.
      if (responseUncertain(briefError) && Number((briefError as { status?: number })?.status) !== 503) {
        // A lost response does not mean the server cancelled generation. Read
        // the persisted result before offering another expensive POST.
        setConnectionNotice('Reconnecting… Checking whether your Market Brief is ready.');
        const deadline = Date.now() + 120_000;
        while (scope.active && Date.now() < deadline) {
          try {
            const next = await loadWorkspace(token, workspaceId);
            if (!scope.active) return;
            if (next.workspace.brief_hypothesis) {
              applyBundle(next);
              setDraftBrief(next.workspace.brief_hypothesis);
              return;
            }
          } catch (recoveryError) {
            if (!scope.active || handleAccessError(recoveryError)) return;
          }
          await new Promise((resolve) => setTimeout(resolve, RADAR_POLL_INTERVAL_MS));
        }
        if (!scope.active) return;
        setConnectionNotice('');
        setError('We could not confirm whether your brief finished. Refresh to check before trying again.');
      } else setError(friendlyError(briefError));
    } finally {
      finish(scope);
    }
  }

  async function confirmBrief() {
    if (!token || !workspaceId || !draftBrief || !begin('confirm')) return;
    const scope = operationScope.current;
    try {
      const confirmedBrief = {
        ...draftBrief,
        products: cleanBriefList(draftBrief.products),
        target_account_types: cleanBriefList(draftBrief.target_account_types),
        required_capabilities: cleanBriefList(draftBrief.required_capabilities),
        exclusions: cleanBriefList(draftBrief.exclusions),
        trigger_types: cleanBriefList(draftBrief.trigger_types),
      };
      const baseline = bundle?.workspace.brief || bundle?.workspace.brief_hypothesis;
      const normalize = (value: string) => value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
      if (baseline && normalize(confirmedBrief.seller_summary) !== normalize(baseline.seller_summary)
          && reviewedBrief.current !== JSON.stringify(confirmedBrief)) {
        setConnectionNotice('Checking that search criteria match your product changes…');
        const review = await radarApi.reviewBrief(token, workspaceId, confirmedBrief);
        if (!scope.active) return;
        reviewedBrief.current = JSON.stringify(review.brief);
        if (review.changed) {
          setDraftBrief(review.brief);
          setCriteriaUpdated(true);
          setConnectionNotice('Search criteria were updated to match your products. Review them below, then confirm.');
          return;
        }
      }
      const workspace = await radarApi.confirmBrief(token, workspaceId, confirmedBrief);
      if (!scope.active) return;
      setFailure((current) => current?.status ? null : current);
      setDraftBrief(confirmedBrief);
      setCriteriaUpdated(false);
      setConnectionNotice('');
      setBundle((current) => current ? { ...current, workspace } : current);
      setEditingBriefVersion(false);setSelectedBriefVersion(null);selectedBriefVersionRef.current=null;
      setActiveSection(bundle?.workspace.brief_email_enabled?'brief':'discover');
      if (bundle?.workspace.brief_email_enabled || bundle?.watches?.length) {
        const next = await loadWorkspace(token, workspaceId);
        if (scope.active) applyBundle(next);
      }
    } catch (briefError) {
      if (scope.active && !handleAccessError(briefError)) {
        setConnectionNotice('');
        setError(friendlyError(briefError));
      }
    } finally {
      finish(scope);
    }
  }

  async function viewBriefResults(version:number|null) {
    if(!token||!workspaceId||pendingRef.current||loadingVersion)return;
    const previous=selectedBriefVersionRef.current;
    selectedBriefVersionRef.current=version;setSelectedBriefVersion(version);setLoadingVersion(true);
    const scope=operationScope.current;
    try {const next=await radarApi.getWorkspace(token,workspaceId,version);if(scope.active){applyBundle(next);setError('');setActiveSection('discover');}}
    catch(err){if(scope.active){selectedBriefVersionRef.current=previous;setSelectedBriefVersion(previous);setError(friendlyError(err));}}
    finally{if(scope.active)setLoadingVersion(false);}
  }

  async function search(version?:number) {
    if (!token || !workspaceId || ['queued', 'processing'].includes(bundle?.latest_run?.status || '') || loadingVersion || !begin('search')) return;
    const previousVersion=selectedBriefVersionRef.current;
    if(version!==undefined){selectedBriefVersionRef.current=version;setSelectedBriefVersion(version);setActiveSection('discover');}
    const scope = operationScope.current;
    // Keep the key when delivery is uncertain. An explicit retry is the same
    // operation and must not consume another allowance.
    const requestVersion=selectedBriefVersionRef.current;
    let key:string|undefined;
    try {
      key=searchKeys.current.get(requestVersion)||crypto.randomUUID();
      searchKeys.current.set(requestVersion,key);
      const run = requestVersion===null
        ? await radarApi.startSearch(token,workspaceId,key)
        : await radarApi.startSearch(token,workspaceId,key,requestVersion);
      if (!scope.active) return;
      setFailure((current) => current?.status ? null : current);
      setBundle((current) => current ? {
        ...current,
        latest_run: { id: run.id, status: run.status as SearchStatus },
      } : current);
      searchKeys.current.delete(requestVersion);
      // Read the authoritative quota (also correct for an idempotent replay).
      try {
        const next = await loadWorkspace(token, workspaceId);
        if (scope.active && next.latest_run?.id === run.id) applyBundle(next);
      }
      catch (readError) {
        if (scope.active && !handleAccessError(readError)) {
          setConnectionNotice('Search started. Reconnecting to update its progress and your allowance…');
        }
      }
    } catch (searchError) {
      if (!scope.active || handleAccessError(searchError)) return;
      if (responseUncertain(searchError)) {
        try {
          const next = await loadWorkspace(token, workspaceId);
          if (!scope.active) return;
          // Even when the POST did not arrive, these rows belong to the
          // selected version. Keep its retry key until the run is confirmed.
          applyBundle(next);
          if (next.latest_run?.idempotency_key === key) {
            searchKeys.current.delete(requestVersion);
            return;
          }
        } catch (recoveryError) {
          if (!scope.active || handleAccessError(recoveryError)) return;
          // Restore the version belonging to the rows still on screen.
          // The per-version key remains available for an explicit retry.
          selectedBriefVersionRef.current=previousVersion;setSelectedBriefVersion(previousVersion);
        }
        setError('We could not confirm the search started. Retry safely; this attempt will not be charged twice.');
      } else {
        searchKeys.current.delete(requestVersion);
        selectedBriefVersionRef.current=previousVersion;setSelectedBriefVersion(previousVersion);
        setError(friendlyError(searchError));
      }
    } finally {
      finish(scope);
    }
  }

  async function feedback(
    candidateId: string,
    verdict: 'relevant' | 'not_fit',
    reasons: string[],
    note?: string
  ) {
    if (!session || !workspaceId || !begin('feedback')) return;
    const scope = operationScope.current;
    try {
      await radarApi.saveFeedback(session.access_token, workspaceId, candidateId, {
        verdict,
        reason_codes: reasons,
        note,
      });
      if (!scope.active) return;
      const next = await loadWorkspace(session.access_token, workspaceId);
      if (!scope.active) return;
      applyBundle(next);
      setFeedbackError('');
    } catch (feedbackError) {
      if (scope.active && !handleAccessError(feedbackError)) setFeedbackError(friendlyError(feedbackError));
    } finally {
      finish(scope);
    }
  }

  async function undoFeedback(candidateId: string) {
    if (!session || !workspaceId || !begin('feedback')) return;
    const scope = operationScope.current;
    try {
      await radarApi.undoFeedback(session.access_token, workspaceId, candidateId);
      if (!scope.active) return;
      const next = await loadWorkspace(session.access_token, workspaceId);
      if (!scope.active) return;
      applyBundle(next);
      setFeedbackError('');
    } catch (feedbackError) {
      if (scope.active && !handleAccessError(feedbackError)) setFeedbackError(friendlyError(feedbackError));
    } finally {
      finish(scope);
    }
  }

  async function mutateWatch(action: () => Promise<unknown>) {
    if (!token || !workspaceId || !begin('watch')) return false;
    const scope = operationScope.current;
    try {
      await action();
      if (!scope.active) return;
      const next = await loadWorkspace(token, workspaceId);
      if (scope.active) applyBundle(next);
      return scope.active;
    } catch (watchError) {
      if (scope.active && !handleAccessError(watchError)) {
        setError(friendlyError(watchError));
        if (responseUncertain(watchError)) {
          try {
            const next = await loadWorkspace(token, workspaceId);
            if (scope.active) applyBundle(next);
          } catch { /* Keep the original failure visible; never repeat a mutation blindly. */ }
        }
      }
      return false;
    } finally { finish(scope); }
  }

  const reconnect = needsReconnect || failure?.retryable ? () => {
    if (openingRef.current || opening) return;
    openingRef.current = true;
    setOpeningSlow(false);
    setOpening(true);
    setConnectionAttempt((attempt) => attempt + 1);
  } : undefined;

  if (!authReady) {
    if (authProblem) return <main className="radar-access-error">
      <span>{t('Buyer Radar')}</span><h1>{t(authProblem.pending ? 'Preparing your workspace…' : 'We could not open this workspace.')}</h1>
      <p role={authProblem.pending ? 'status' : 'alert'}>{e(authProblem.message)}</p>
      {authProblem.pending
        ? <button type="button" onClick={() => window.location.reload()}>{t('Reload page')}</button>
        : <button type="button" onClick={() => {
          if (authPending.current) return;
          authPending.current = true;
          setAuthProblem(null);
          setAuthAttempt(attempt => attempt + 1);
        }}>{t('Reconnect')}</button>}
    </main>;
    return <div className="radar-full-loader" role="status"><LoaderCircle className="spin" />{' '}{t("Preparing your workspace…")}</div>;
  }
  if (!session) return <RadarSignIn initialError={authCallbackError} />;
  if (opening && !bundle) {
    if (openingSlow) return <main className="radar-access-error">
      <span>{t('Buyer Radar')}</span><h1>{t('Opening your private radar…')}</h1>
      <p role="status">{t('This request is taking longer than expected. Its result is not yet known.')}</p>
      <button type="button" onClick={() => window.location.reload()}>{t('Reload page')}</button>
    </main>;
    return <div className="radar-full-loader" role="status"><LoaderCircle className="spin" />{' '}{t("Opening your private radar…")}</div>;
  }
  if (!bundle) {
    return (
      <main className="radar-access-error">
        <span>{t("Buyer Radar")}</span><h1>{t("We could not open this workspace.")}</h1>
        <p role="alert">{error ? e(error) : t("Please try again. If this continues, contact support.")}</p>
        {reconnect && <button type="button" onClick={reconnect} disabled={opening}>{t("Reconnect")}</button>}
        <button type="button" onClick={onSignOut} disabled={signingOut}>{t(signingOut ? 'Logging out…' : 'Use another account')}</button>
      </main>
    );
  }

  const brief = draftBrief || bundle.workspace.brief || bundle.workspace.brief_hypothesis;
  if (!brief) {
    return (
      <RadarBriefStarter
        workspaceId={workspaceId!}
        merchantName={bundle.workspace.merchant_name}
        busy={pending === 'brief'}
        connectionNotice={connectionNotice}
        error={error}
        onReconnect={reconnect}
        onSubmit={generateBrief}
      />
    );
  }

  const journeyEnabled=bundle.workspace.workspace_journey_enabled===true || bundle.workspace.brief_email_enabled===true;
  const WorkspaceView=journeyEnabled?RadarWorkspaceView:LegacyWorkspaceView;
  const OutreachPanel=journeyEnabled?RadarOutreachPanel:LegacyOutreachPanel;
  return (
    <RadarJourneyProvider enabled={journeyEnabled} customer={bundle.workspace.customer_workspace_enabled===true}>
    <RadarOutreachFlowProvider key={userId + '/' + workspaceId} enabled={bundle.workspace.outreach_enabled===true && bundle.workspace.brief_status==='confirmed'} onNavigate={section=>{if(section==='act')setActOpened(true);setActiveSection(section);}}>
    <RadarDisplayProvider key={userId + "/" + workspaceId} token={token || undefined} bundle={bundle}
      section={activeSection || (bundle.workspace.brief_status === 'confirmed' ? 'discover' : 'brief')}>
      <WorkspaceView
        workspaceId={workspaceId}
        briefPanel={bundle.workspace.brief_email_enabled && !editingBriefVersion && bundle.brief_versions?.length ?
          <RadarBriefHistory versions={bundle.brief_versions} currentRevision={bundle.workspace.brief_revision}
           busy={!!pending||loadingVersion} onEdit={version=>{setDraftBrief(version.brief);setEditingBriefVersion(true);}}
           onResults={version=>void viewBriefResults(version)} onSearch={version=>void search(version)}/> : undefined}
        versionFilter={bundle.workspace.brief_email_enabled?<label className="radar-version-filter">{t('Results for brief')}
          <select aria-label={t('Results for brief')} value={selectedBriefVersion??''} disabled={!!pending||loadingVersion}
           onChange={event=>void viewBriefResults(event.target.value?Number(event.target.value):null)}>
           <option value="">{t('All versions')}</option>{bundle.brief_versions?.map(v=><option key={v.version} value={v.version}>v{v.version} · {v.brief.target_market}</option>)}</select>
          {loadingVersion&&<span role="status">{t('Loading…')}</span>}</label>:undefined}
        researchPanel={(bundle.workspace.research_enabled||bundle.workspace.internal_research_enabled) && token && workspaceId ?
          <RadarResearchSettings key={userId+'/'+workspaceId} token={token} workspaceId={workspaceId}
            onChanged={()=>{void loadWorkspace(token,workspaceId).then(applyBundle).catch(()=>{});}}/> : undefined}
        monitorPanel={journeyEnabled ? <RadarMonitorWorkspace token={token!} workspaceId={workspaceId!}
          watches={bundle.watches || []} summary={bundle.watch_summary || {active_count:0,limit:40}}
          plan={bundle.signal_plan} focusedWatchId={watchFocus} busy={pending === 'watch' || savingFeedback || pending === 'confirm'}
          onDiscover={()=>setActiveSection('discover')}
          onSignalFeedback={(id,verdict)=>mutateWatch(()=>radarApi.signalFeedback(token!,workspaceId!,id,verdict))}
          onStatus={(id,status)=>void mutateWatch(()=>radarApi.setWatchStatus(token!,workspaceId!,id,status))}
          onConfirm={ids=>void mutateWatch(()=>radarApi.confirmWatches(token!,workspaceId!,ids))}
          onFocus={focus=>void mutateWatch(()=>radarApi.updateFocus(token!,workspaceId!,{
            ...focus,summary:focus.summary.trim(),people_focus:focus.people_focus.trim(),topics:cleanBriefList(focus.topics),
          }))}/> : undefined}
        currentSearchRunId={bundle.latest_run?.id}
        merchantName={bundle.workspace.merchant_name}
        brief={(activeSection||'discover')==='discover'&&selectedBriefVersion!==null
          ? bundle.brief_versions?.find(v=>v.version===selectedBriefVersion)?.brief||brief : brief}
        briefStatus={bundle.workspace.brief_status}
        activeSection={activeSection || (bundle.workspace.brief_status === 'confirmed' ? 'discover' : 'brief')}
        onSectionChange={section => {if(section === 'act')setActOpened(true);setActiveSection(section);}}
        onActContact={id => {setActContact(current => ({id, revision: current.revision + 1}));setActOpened(true);setActiveSection('act');}}
        watchFocusId={watchFocus}
        actPanel={(actOpened || (bundle.workspace.outreach_enabled && bundle.workspace.brief_status==='confirmed')) ? <EmailSignalExportContext.Provider value={bundle.workspace.email_signals_enabled === true}>{bundle.workspace.outreach_enabled ? <OutreachPanel key={userId + '/' + workspaceId} token={token!} workspaceId={workspaceId!}
          briefEmailEnabled={bundle.workspace.brief_email_enabled===true} merchantName={bundle.workspace.merchant_name} brief={bundle.workspace.brief_status === 'confirmed' ? bundle.workspace.brief : null}
          candidates={bundle.candidates} active={activeSection === 'act'} loadWhileInactive={journeyEnabled||!activeSection||activeSection==='discover'}/> : <RadarActPanel key={userId + '/' + workspaceId} token={token!} workspaceId={workspaceId!}
          outcomeUsage={liveUsage.outcome} active={activeSection === 'act'} watches={bundle.watches || []} merchantName={bundle.workspace.merchant_name} initialContactId={actContact.id} contactSelectionRevision={actContact.revision}
          confirmedBrief={bundle.workspace.brief_status === 'confirmed' ? bundle.workspace.brief : null}
          sellerSourceUrl={bundle.workspace.seller_source_url || bundle.workspace.brief_provenance?.source_url}
          onWatch={id => {setWatchFocus(id);setActiveSection('watch');}}/>}
          {bundle.workspace.customer_workspace_enabled && bundle.access && bundle.access.access_type !== 'self_serve_trial' &&
            <details key={'earlier/'+userId+'/'+workspaceId} className="radar-act-history"
              onToggle={event=>{if(event.currentTarget.open)setEarlierActOpened(true);}}>
              <summary>{t('Earlier saved drafts and contacts')}</summary>
              {earlierActOpened && <RadarActPanel key={'earlier/'+userId+'/'+workspaceId}
                token={token!} workspaceId={workspaceId!} outcomeUsage={liveUsage.outcome}
                active={activeSection==='act'} watches={bundle.watches || []} merchantName={bundle.workspace.merchant_name}
                confirmedBrief={bundle.workspace.brief_status==='confirmed'?bundle.workspace.brief:null}
                sellerSourceUrl={bundle.workspace.seller_source_url || bundle.workspace.brief_provenance?.source_url}
                onWatch={id=>{setWatchFocus(id);setActiveSection('watch');}}/>}
            </details>}
          {!bundle.workspace.outreach_enabled&&<RadarEmailSignals key={'signals/'+userId+'/'+workspaceId} token={token!} workspaceId={workspaceId!}
            enabled={bundle.workspace.email_signals_enabled === true} active={activeSection === 'act'}/>}</EmailSignalExportContext.Provider> : undefined}
        hasUnsavedBriefChanges={bundle.workspace.brief_status === 'confirmed'
          && JSON.stringify(brief) !== JSON.stringify(bundle.workspace.brief)}
        onDiscardBrief={() => {
          setDraftBrief(bundle.workspace.brief || null);setEditingBriefVersion(false);
          reviewedBrief.current = null;
          setCriteriaUpdated(false);
          setConnectionNotice('');
          setActiveSection('discover');
        }}
        candidates={bundle.candidates}
        watches={bundle.watches}
        watchSummary={bundle.watch_summary}
        signalPlan={bundle.signal_plan}
        savingWatch={pending === 'watch'}
        onWatch={(id) => void mutateWatch(() => radarApi.addWatch(token!, workspaceId!, id))}
        onSignalFeedback={(id, verdict) => void mutateWatch(() => radarApi.signalFeedback(token!, workspaceId!, id, verdict))}
        onWatchConfirm={(ids) => void mutateWatch(() => radarApi.confirmWatches(token!, workspaceId!, ids))}
        onWatchStatus={(id, status) => void mutateWatch(() => radarApi.setWatchStatus(token!, workspaceId!, id, status))}
        onFocus={(focus: SignalFocus) => void mutateWatch(() => radarApi.updateFocus(token!, workspaceId!, {
          ...focus, summary: focus.summary.trim(), people_focus: focus.people_focus.trim(), topics: cleanBriefList(focus.topics),
        }))}
        searchStatus={bundle.latest_run?.status || 'idle'}
        searchError={bundle.latest_run?.error_message}
        requestError={error}
        feedbackError={feedbackError}
        savingFeedback={savingFeedback}
        onReconnect={reconnect}
        connectionNotice={connectionNotice}
        confirming={pending === 'confirm'}
        startingSearch={pending === 'search'}
        discoverySummary={bundle.discovery_summary}
        assumptions={bundle.workspace.brief_provenance?.assumptions}
        reviewCriteria={criteriaUpdated}
        searchesRemaining={searchesRemaining}
        usage={{access:bundle.access, searchesRemaining, monitoring:bundle.watch_summary, ...liveUsage,
          billing:token && workspaceId ? {token,workspaceId,...billingReturn,onUsageChanged:usage=>setBundle(previous=>previous?.workspace.id===workspaceId?{...previous,usage}:previous)} : undefined}}
        onBriefChange={next => {
          radarAnalytics.once('radar_brief_edit_started', `${workspaceId}:brief`, { workspace_id: workspaceId, screen: 'brief', is_first: bundle.workspace.brief_status !== 'confirmed' });
          setDraftBrief(next);
        }}
        onConfirmBrief={confirmBrief}
        onSearch={()=>void search()}
        onFeedback={feedback}
        onUndoFeedback={undoFeedback}
      />
    </RadarDisplayProvider>
    </RadarOutreachFlowProvider>
    </RadarJourneyProvider>
  );
}

function RadarBriefStarter({
  workspaceId,
  merchantName,
  busy,
  error,
  connectionNotice,
  onSubmit,
  onReconnect,
}: {
  workspaceId: string;
  merchantName: string;
  busy: boolean;
  error: string;
  connectionNotice: string;
  onReconnect?: () => void;
  onSubmit: (input: {
    seller_url?: string;
    target_market?: string;
    relationship_goal?: string;
    seller_notes?: string;
  }) => void;
}) {
  const { t, e } = useRadarLocale();
  const [sellerUrl, setSellerUrl] = useState('');
  const [market, setMarket] = useState('');
  const [relationship, setRelationship] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceError, setSourceError] = useState('');

  return (
    <main className="radar-starter-page">
      <header className="radar-starter-brand">
        <img src="/images/mindstream_logomark.png" alt="" />
        <span>Mindstream</span><i /><strong>{t("Buyer Radar")}</strong>
      </header>
      <section className="radar-starter-card">
        <span className="radar-workspace-eyebrow">{t("Understand ·")}{' '}{merchantName}</span>
        <h1>{t("Let’s build a first hypothesis about your market.")}</h1>
        <p>{t("Share a product page, describe what you sell, or provide both. We will draft a Market Brief for you to review.")}</p>
        <p className="radar-input-language-hint">{t('You can write in English, Simplified Chinese, Traditional Chinese or Cantonese.')}</p>
        <div className="radar-starter-help"><RadarQuickStartLink/></div>
        <form onChange={() => radarAnalytics.once('radar_brief_edit_started', `${workspaceId}:brief_start`, {
          workspace_id: workspaceId, screen: 'brief_start', is_first: true,
        })} onSubmit={(event) => {
          event.preventDefault();
          if (busy) return;
          if (!sellerUrl.trim() && !notes.trim()) {
            setSourceError('Provide a product link or describe what you sell.');
            return;
          }
          setSourceError('');
          onSubmit({
            seller_url: sellerUrl.trim() || undefined,
            target_market: market.trim() || undefined,
            relationship_goal: relationship.trim() || undefined,
            seller_notes: notes.trim() || undefined,
          });
        }}>
          <label><span>{t("Company or product page")}</span><input type="url" disabled={busy} value={sellerUrl} onChange={(event) => setSellerUrl(event.target.value)} placeholder="https://yourcompany.com/product" /></label>
          <label><span>{t("Product description")}</span><GrowingTextarea disabled={busy} maxLength={4000} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={t("Describe your main products, uses and any customization you offer. No website needed.")} /></label>
          <small>{t("At least one is required: a product link or a product description.")}</small>
          {sourceError && <div className="radar-auth-message" role="alert">{t(sourceError)}</div>}
          <div className="radar-starter-row">
            <label><span>{t("Target market")}</span><GrowingTextarea disabled={busy} value={market} onChange={(event) => setMarket(event.target.value)} placeholder={t("e.g. Vietnam")} /></label>
            <label><span>{t("Who do you hope to find?")}</span><GrowingTextarea value={relationship} onChange={(event) => setRelationship(event.target.value)} placeholder={t("e.g. implementation distributor")} /></label>
          </div>
          <button className="radar-primary-action" type="submit" disabled={busy}>
            {busy ? <><LoaderCircle className="spin" size={18} />{' '}{t("Reading your material and drafting…")}</> : <>{t("Draft my Market Brief")}{' '}<ArrowRight size={18} /></>}
          </button>
          {busy && <div className="radar-search-state" role="status"><div>
            <strong>{t(connectionNotice || "Reading your material and drafting…")}</strong>
            <span>{t("This can take about a minute. You can stay here; the next step opens automatically.")}</span>
          </div></div>}
        </form>
        {error && <div className="radar-auth-message" role="alert">{e(error)}
          {onReconnect && <button type="button" className="radar-text-button" onClick={onReconnect}>{t("Reconnect")}</button>}
        </div>}
      </section>
    </main>
  );
}
